import axios from 'axios';
import { debounce } from '@/utils/debounce';
// 假设使用vue-router进行路由跳转，根据实际项目导入
import router from '@/router';

// 创建 axios 实例
const service = axios.create({
    baseURL: '/api',
    timeout: 20000,
});

// 请求拦截器
service.interceptors.request.use(
    (config) => {
        // 可在此统一添加 token（示例）
        const token = localStorage.getItem('token');
        if (token) config.headers.Authorization = `Bearer ${token}`;
        
        // 处理防抖配置
        if (config.debounce) {
            const wait = typeof config.debounce === 'number' ? config.debounce : 300;
            const debounceKey = `${config.url}_${JSON.stringify(config.params || config.data)}`;
            
            if (!service.debounceMap) {
                service.debounceMap = {};
            }
            
            if (!service.debounceMap[debounceKey]) {
                service.debounceMap[debounceKey] = debounce(
                    (resolve) => {
                        // 移除已使用的防抖函数
                        delete service.debounceMap[debounceKey];
                        // 继续正常请求流程
                        resolve();
                    }, 
                    wait
                );
            }
            
            return new Promise((resolve, reject) => {
                service.debounceMap[debounceKey](resolve, reject);
            }).then(() => {
                // 防抖等待结束后继续请求
                return config;
            }).catch(() => {
                // 防抖期间请求被取消
                return Promise.reject({
                    message: '请求被防抖取消',
                    code: 'DEBOUNCE_CANCEL',
                    config
                });
            });
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    (response) => {
        return response.data; // 直接返回数据
    },
    (error) => {
        // 如果是 CanceledError（axios 取消请求的错误），直接抛出，不进行错误处理
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }
        
        let errorMessage = '请求失败，请稍后重试';

        // 1. 处理超时错误
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            errorMessage = '请求超时，请检查网络或稍后重试';
        }
        // 2. 处理HTTP状态码错误
        else if (error.response) {
            switch (error.response.status) {
                case 401:
                    errorMessage = '未授权，请重新登录';
                    // 清空所有缓存
                    localStorage.clear();
                    sessionStorage.clear();
                    // 跳转到首页，使用replace避免回退到原页面
                    router.replace('/home');
                    break;
                case 403:
                    errorMessage = '拒绝访问';
                    break;
                case 404:
                    errorMessage = '请求资源不存在';
                    break;
                case 500:
                    errorMessage = '服务器错误';
                    break;
            }
        }
        // 3. 无响应（网络断开或后端未启动）
        else if (error.request) {
            errorMessage = '网络连接异常，请检查网络';
        }
        // 4. 处理防抖取消的错误
        else if (error.code === 'DEBOUNCE_CANCEL') {
            return Promise.reject(error);
        }

        // 返回一个更结构化的错误对象
        return Promise.reject({
            message: errorMessage,
            code: error.response?.status || 'NETWORK_ERROR',
            originalError: error,
        });
    }
);

// 封装带防抖的请求方法（支持 signal）
const createDebounceRequest = (method) => (url, dataOrParams, config = {}) => {
    return service({
        url,
        method,
        [method === 'get' ? 'params' : 'data']: dataOrParams,
        ...config
    });
};

// 导出常用的方法
export const get = createDebounceRequest('get');
export const post = createDebounceRequest('post');
export const put = createDebounceRequest('put');
export const del = createDebounceRequest('delete');

export default service;